import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Alert from '../../components/Alert';
import { If, Else, Then } from 'react-if';

import { setStorageWithTtl, getStorage } from '../../helpers/Storage';
import validations from '../../helpers/Validations';

import StepClass from '../../entities/Step';
import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import TooltipInfo from '../../components/Tooltip';
import Input from '../../components/Input';
import Password from '../../components/Password';
import LoadingDefault from '../../components/LoadingDefault';
import Typography from '../../components/Typography';
import AuthService from '../../services/AuthService';
import ServiceRegistration from '../../services/RegisterService';
import HtmlInput from '../../interfaces/HtmlInput';
import { User } from '../../interfaces/User';

import DefaultErrorData from './DefaultError';

import {
  gtmAttributesHiddenPassword,
  gtmAttributesWhatsappInfo,
  gtmAttributesTooltipEmail,
  gtmAttributesInvalidPassword,
  gtmAttributesFieldsRequired,
  gtmAttributesUpdateRegisterPage,
} from '../../utils/gtmVariables';

import useGTMEventsTracker from '../../hooks/useGTMEventsTracker';

import AppConfig from '../../config/AppConfig';
import Container from '../../components/Container';

const Register = () => {
  const [user, setUser] = useState<User>({
    cnpj: '',
    email: '',
    nome: '',
    cellphone: '',
    phone: '',
    password: '',
    optInWhats: false,
    soldBy: '',
  } as User);

  const [formValidation, setFormValidation] = useState({
    cellphone: false,
    phone: false,
    password: false,
  });

  const [check, setCheck] = useState(false);
  const [isLogged, setIsLogged] = useState(getStorage('isLogged'));
  const [disableButton, setDisableButton] = useState(false);
  const [btnText, setBtnText] = useState('Continuar');
  const [messages, setMessages] = useState<any>([]);

  const navigate = useNavigate();
  const location = useLocation();

  const search = new URLSearchParams(location.search);
  const tokenBase64 = search.get('token') || '';
  const coupon_name = search.get('coupon_name') || '';
  const commissioned_user_id = search.get('commissioned_user_id') || '';

  const serviceRegistration = new ServiceRegistration();
  const Step = new StepClass();
  const gtmEventsTracker = useGTMEventsTracker();

  useEffect(() => {
    gtmEventsTracker(gtmAttributesUpdateRegisterPage);
  }, []);

  const loginSuccess = (seller: any) => {
    navigate('/pas/home', {
      state: { type: seller.contract.seller_entrance },
    });
  };

  const loginError = () => {
    navigate('/pas/error');
  };

  const sendToStep = () => {
    const data = JSON.parse(getStorage('seller'));
    const seller = data.seller;

    try {
      if (seller.step.currentStep === 'Modal ao PAS') {
        loginSuccess(seller);
      }

      if (seller.complianceStatus === 'reproved') {
        return navigate('/compliance/invalid/cnpj');
      }

      const isHunting = seller.contract.sellerEntrance === 'hunting';
      const isContractStep = seller.step.currentStep === 'Contrato';
      const isFreightStep = seller.step.currentStep === 'Frete';

      if (isHunting && isContractStep) {
        if (seller.contract.type) {
          return navigate('/hunting/contract', {
            state: { contract: seller.contract },
          });
        } else {
          AuthService.logout();
          return navigate('/hunting/email/confirmation');
        }
      }

      let url = Step.getStep(seller.step.currentStep)?.url ?? '/';

      let params = isContractStep ? { state: { contract: seller.contract } } : {};

      if (isFreightStep) {
        url += 'envvias';
      }
      return navigate(url, params);
    } catch {
      addMessage(
        'Ocorreu um erro inesperado no sistema.<strong> Por favor, tente novamente!</strong >',
        'warning'
      );
      return AuthService.logout();
    }
  };

  useEffect(() => {
    if (tokenBase64) {
      serviceRegistration
        .activate(tokenBase64, {})
        .then((data) => {
          setIsLogged(true);
          AuthService.setLocalSeller(data);
          sendToStep();
        })
        .catch((err) => {
          const activated = err.response.data?.error === 'token_already_used';
          if (activated) {
            navigate('/confirmation-account');
          }
        });
      return;
    }
    if (isLogged) {
      sendToStep();
    }
  }, []);

  const clickCheck = (e: ChangeEvent<HTMLInputElement>) => {
    setCheck(e.target.checked);
    setUser({
      ...user,
      optInWhats: e.target.checked,
    });
  };

  const isValidAttributes = {
    verifyAttribute: function (attribute: string) {
      gtmAttributesFieldsRequired.erro = `Voce precisa preencher o campo ${attribute}`;
      gtmEventsTracker(gtmAttributesFieldsRequired);
    },
  };

  const addMessage = (message: any, variant: string) => {
    setMessages((prevMessages: any) => [...prevMessages, { message, variant }]);
    clearMessages();
  };

  const removeMessage = (index: number) => {
    setMessages((prevMessages: any) => {
      const updatedMessages = [...prevMessages];
      updatedMessages.splice(index, 1);
      return updatedMessages;
    });
  };

  const clearMessages = () => {
    setTimeout(() => {
      setMessages([]);
    }, 4000);
  };

  const statusMessage = (error?: DefaultErrorData) => {
    let mainCause: string = error?.mensagem
      ? error.mensagem
      : 'Um erro inesperado aconteceu. Tente novamente mais tarde.';
    /**
     * includes('40') will catch all those status "240, 400, 404, 401, 402, 403, 405, 406, 407, 408, 409, 540"
     * "240" (Invalid Media Type) and "540" (Unknown Error), but they will not happen here
     */
    if (error?.codigo?.includes('40') && error.motivos.length > 0) {
      error.motivos.map((element) => {
        addMessage(element, 'danger');
      });
    } else {
      addMessage(mainCause, 'danger');
    }
    /* Atualiza o DOM */
    setTimeout(() => {
      setBtnText('Continuar');
      setDisableButton(false);
    }, 3500);
  };

  const handleSubmit = async (event: any) => {
    setBtnText('Aguarde...');
    event.preventDefault();

    if (!formValidation.password) {
      gtmEventsTracker(gtmAttributesInvalidPassword);
      setBtnText('Continuar');
      addMessage('<strong>Senha inválida</strong>', 'warning');
    }

    if (!formValidation.phone || !formValidation.cellphone) {
      setBtnText('Continuar');
      addMessage('Por favor, revise as informações do formulário', 'warning');
    }

    if (validateUser(user)) {
      setDisableButton(true);
      await registerSeller();
      return;
    }
    const valid = isValidAttributes['verifyAttribute'];
    Object.keys(user).forEach((value: any) => {
      // @ts-expect-error: Unreachable code error
      if (!user[value]) {
        valid(value);
      }
    });
    setDisableButton(false);
    setBtnText('Continuar');
    addMessage('Preencha os campos do formulário', 'warning');
  };

  const validateUser = (user: User) => {
    return Object.values(user).every((value) => value !== '');
  };

  const registerSeller = async () => {
    const body = {
      coupon: {},
      document: user.cnpj,
      sold_by: user.soldBy,
      optinWhats: check,
      responsible: {
        cellphone: user.cellphone,
        email: user.email,
        name: user.nome,
        password: user.password,
        phone: user.phone,
      },
    };

    coupon_name && commissioned_user_id
      ? Object.assign(body.coupon, {
          coupon_name: coupon_name,
          commissioned_user_id: commissioned_user_id,
        })
      : Object.assign(body.coupon, {
          coupon_name: coupon_name,
        });

    if (body.optinWhats) {
      gtmEventsTracker(gtmAttributesWhatsappInfo);
    }
    const res = await serviceRegistration.createSeller(body, statusMessage);

    if (res === undefined) return;

    setStorageWithTtl('sellerEmail', user.email, 1440);
    setStorageWithTtl('sellerId', res?.data?.conteudo.id, 1440);
    setStorageWithTtl('sellerName', user.nome, 1440);
    setStorageWithTtl('seller', JSON.stringify(res?.data.conteudo), 1440);
    navigate('/confirmation-email');
  };

  const validateInputValue = (value: string, name: string) => {
    if (name == 'nome') return validations.onlyLetters(value);
    if (name == 'cnpj') return validations.onlyAlphanumeric(value);
  };

  const handleChange = useCallback(
    (e: any) => {
      if (e.target && typeof e.target.value !== undefined) {
        setUser({
          ...user,
          [e.target.name]: ['nome', 'cnpj', 'email'].includes(e.target.name)
            ? validateInputValue(e.target.value, e.target.name)
            : e.target.value,
        });
      }
    },
    [user]
  );

  const setEmailValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target && typeof e.target.value !== undefined) {
      setUser({
        ...user,
        [e.target.name]: validations.toLowerCase(e),
      });
    }
  };

  const setPasswordValue = (e: any) => {
    if (e?.target?.value) {
      setUser({
        ...user,
        password: e.target.value,
      });
    }
    if (e?.isValid) {
      setFormValidation({ ...formValidation, password: e.isValid });
    }
  };

  const validCellphone = (e: any) => {
    if (e?.target?.value) {
      setUser({
        ...user,
        cellphone: validations.onlyNumbers(e.target.value),
      });
    }
    setFormValidation({ ...formValidation, cellphone: e.isValid });
  };

  const validPhone = (e: any) => {
    if (e?.target?.value) {
      setUser({
        ...user,
        phone: validations.onlyNumbers(e.target.value),
      });
    }
    setFormValidation({ ...formValidation, phone: e.isValid });
  };

  return (
    <If condition={isLogged}>
      <Then>
        <LoadingDefault />
      </Then>
      <Else>
        <div id="mp__register">
          <Container className="flex justify-center flex-col gap-6 sm:gap-2 sm:items-center sm:text-center mt-6 mb-12 sm:mt-12 sm:mb-16">
            <Typography variant="title1">Cadastro</Typography>
            <Typography id="mp__form-text-intro">
              Pra começar, informe os dados solicitados. É necessário preencher todos os campos.
            </Typography>
          </Container>
          <Container as="section" className="flex justify-center items-center mb-16 sm:mb-44">
            <form noValidate onSubmit={handleSubmit} className="w-full">
              <div className="w-full flex flex-col gap-6">
                <div className="relative sm:max-w-md sm:mx-auto w-full">
                  <Input
                    id="mp__input-register_email"
                    labelId="mp__label-email"
                    label="Email"
                    type="email"
                    name="email"
                    value={user.email}
                    rules={[(val: HtmlInput) => validations.isValidEmail(val, true)]}
                    onChange={(e: any) => setEmailValue(e)}
                  >
                    <TooltipInfo
                      className="absolute right-3 top-1/2 -translate-y-1/2 size-[18px]"
                      text={
                        <>
                          É com ele que você vai <b>acessar a sua loja</b> no portal do lojista.
                        </>
                      }
                      onClick={() => gtmEventsTracker(gtmAttributesTooltipEmail)}
                    />
                  </Input>
                </div>
                <div className="relative sm:max-w-md sm:mx-auto w-full">
                  <Input
                    id="mp__input-register_name"
                    labelId="mp__label-nome-sobrenome"
                    label="Nome e sobrenome"
                    type="text"
                    name="nome"
                    rules={[(val: HtmlInput) => validations.required(val, 'nome e sobrenome')]}
                    onChange={handleChange}
                    value={user.nome}
                  />
                </div>
                <div className="relative sm:max-w-md sm:mx-auto w-full grid sm:grid-cols-2 gap-4">
                  <Input
                    id="mp__input-register_cell"
                    labelId="mp__label-celular"
                    type="text"
                    name="cellphone"
                    onChange={(e: any) => validCellphone(e)}
                    maskType="cellphone"
                    label="Celular"
                    placeholder="DD 99999 9999"
                    rules={[
                      (val: HtmlInput) => validations.length(val, 15, 'celular'),
                      (val: HtmlInput) => validations.required(val, 'celular'),
                      (val: HtmlInput) => validations.isNotPhoneNumber(val, 'celular'),
                    ]}
                  />
                  <Input
                    id="mp__input-register_phone"
                    labelId="mp__label-telefone"
                    type="text"
                    name="phone"
                    onChange={(e: any) => validPhone(e)}
                    maskType="phone"
                    label="Telefone"
                    placeholder="DD 9999 9999"
                    rules={[
                      (val: HtmlInput) => validations.length(val, 14, 'telefone'),
                      (val: HtmlInput) => validations.required(val, 'telefone'),
                      (val: HtmlInput) => validations.isNotPhoneNumber(val, 'telefone'),
                    ]}
                  />
                </div>
                <div className="relative sm:max-w-md sm:mx-auto w-full">
                  <Input
                    id="mp__input-register_cnpj"
                    labelId="mp__label-cnpj"
                    type="text "
                    name="cnpj"
                    onChange={handleChange}
                    maskType="cnpj"
                    label="CNPJ"
                    placeholder="99.999.999/0001-99"
                    rules={[
                      (val: HtmlInput) => validations.length(val, 18, 'CNPJ'),
                      (val: HtmlInput) => validations.required(val, 'CNPJ'),
                    ]}
                  />
                </div>
                <div className="relative sm:max-w-md sm:mx-auto w-full">
                  <Input
                    id="mp__input-register_sold-delivered-by"
                    labelId="mp__label-vendido-entregue-por"
                    type="text "
                    name="soldBy"
                    onChange={handleChange}
                    label="Vendido e entregue por"
                    rules={[(val: HtmlInput) => validations.required(val, 'Vendido entregue por')]}
                  />
                </div>
                <div className="relative sm:max-w-md sm:mx-auto w-full">
                  <Password
                    onChange={(e: any) => setPasswordValue(e)}
                    loginPassword={false}
                    required
                    gtmAttributes={gtmAttributesHiddenPassword}
                  />
                </div>
              </div>
              <div className="relative max-w-md mx-auto w-full py-12">
                <Checkbox
                  id="mp__label-whatsapp-optin"
                  label="Aceito receber informações pelo WhatsApp"
                  value={check}
                  onChange={clickCheck}
                />
              </div>
              <div className="flex justify-start max-w-md mx-auto">
                <Button
                  id="mp__btn-submit_register"
                  text={btnText}
                  type="submit"
                  variant="primary"
                  classNames="w-full rounded-lg"
                  disabled={disableButton}
                />
              </div>
            </form>
          </Container>
          <div className="absolute top-[3.688rem] right-[5.5rem] w-[23.625rem] max-[768px]:top-[4.688rem] max-[768px]:w-full max-[768px]:right-0 max-[768px]:px-5 max-[768px]:py-6">
            {messages.map((messageObj: any, index: number) => (
              <Alert
                key={index}
                variant={messageObj.variant}
                onClose={() => {
                  removeMessage(index);
                }}
                dismissible
              >
                <div dangerouslySetInnerHTML={{ __html: messageObj.message }} />
              </Alert>
            ))}
          </div>
        </div>
      </Else>
    </If>
  );
};

export default Register;
